import { evaluateRetailTradingGate, type RetailTradingGate } from '@/lib/retailSales/retailTradingGate';

/**
 * "Under construction", wherever a shopper can meet a Shopaholic store.
 *
 * The retail twin of `StayGoLiveBadge`, for the same reason: the trading gate
 * stops an unverified seller taking money, but it does not stop a shopper
 * trying. A shopper who searches for a product lands on a result card, and the
 * shop is reachable from there — so the notice has to appear on the card and
 * not only on the storefront.
 *
 * There is no state here. Whether the store is open is derived by
 * `evaluateRetailTradingGate` — the same function the checkout endpoints and
 * the WhatsApp hand-off enforce. When the seller's ID is approved and their
 * business proof lands, every surface clears at the same instant, because none
 * of them was storing anything to be updated.
 */

export interface RetailGoLiveBadgeProps {
  gate: Pick<RetailTradingGate, 'open'>;
  /** `card` is the compact result-tile form; `ribbon` is the full banner. */
  variant?: 'card' | 'ribbon';
  className?: string;
}

export function RetailGoLiveBadge({ gate, variant = 'card', className = '' }: RetailGoLiveBadgeProps) {
  if (gate.open) {
    return null;
  }

  if (variant === 'ribbon') {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 border-b border-amber-300 bg-amber-100 px-5 py-2">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-900">
            Under construction
          </span>
        </div>
        <div className="px-5 py-5">
          <p className="text-base font-semibold text-amber-950">
            This shop is being set up on Zamzi Connect
          </p>
          <p className="mt-2 text-sm leading-relaxed text-amber-900">
            It is not open for orders yet. When it opens you will order directly from the seller
            here - that is how we keep your orders and your details protected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-1.5 rounded-lg border border-amber-400 bg-amber-50 px-2 py-1 ${className}`}
      role="status"
    >
      <span
        aria-hidden
        className="mt-[3px] block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-600"
      />
      <span className="min-w-0">
        <span className="block text-[10px] font-bold uppercase leading-tight tracking-wider text-amber-900">
          Under construction
        </span>
        <span className="block text-[10px] leading-tight text-amber-800">
          Not open for orders yet
        </span>
      </span>
    </div>
  );
}

/**
 * Convenience for list surfaces, which hold a whole profile and would otherwise
 * each re-implement the derivation.
 */
export function retailGoLiveGateFor(profile: Parameters<typeof evaluateRetailTradingGate>[0]) {
  return evaluateRetailTradingGate(profile);
}
